import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";

import { DescriptionObject } from "shared/api/api";
import {
  useListLaborCodes,
  useListParts,
  UsePaginatedAPIState,
} from "shared/api/hooks";
import { useListSignalEvents } from "shared/api/signalEvents/hooks";
import { DataType, SortBy } from "shared/types";

import ClaimAnalyticsLink from "pages/shared/analytics/ClaimAnalyticsLink";

import APIError from "features/ui/APIError";
import {
  filterStateToFilterGroupState,
  getFiltersQuery,
} from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator } from "features/ui/Filters/types";
import Label from "features/ui/Label";
import MaterialTable, { RowData, SchemaEntry } from "features/ui/MaterialTable";

interface Props {
  title?: string | JSX.Element;
  fieldName: string;
  values: string[];
  searchString: string;
  linkToClaimAnalytics?: boolean;
}

const dataFetchHookMap: Record<
  string,
  (args: any) => UsePaginatedAPIState<any[]>
> = {
  laborCode: useListLaborCodes,
  failedPartNumber: useListParts,
  nonCausalParts: useListParts,
  mentionedSignalEvents: useListSignalEvents,
  relatedSignalEvents: useListSignalEvents,
  signalEvents: useListSignalEvents,
  signalEventID: useListSignalEvents,
};

const getDescriptionForID = (
  value: string,
  descriptionsData?: DescriptionObject[]
): string => {
  if (!descriptionsData) {
    return "";
  }

  const foundDescriptionObject = descriptionsData.find((d) => d.ID === value);
  if (foundDescriptionObject && foundDescriptionObject.description) {
    return foundDescriptionObject.description;
  }

  return "";
};

// Create a constant array with keys
export const SUPPORTED_DESCRIPTION_TABLE_FIELD_NAMES: (keyof typeof dataFetchHookMap)[] =
  Object.keys(dataFetchHookMap);

const FilterDescriptionTable = ({
  title,
  fieldName,
  values,
  searchString,
  linkToClaimAnalytics,
}: Props) => {
  const { t } = useTranslation();
  const dataFetchHook: (args: any) => UsePaginatedAPIState<any[]> =
    dataFetchHookMap[fieldName];

  const { data, isLoading, error } = dataFetchHook({
    filter: getFiltersQuery(
      filterStateToFilterGroupState({
        ID: { operator: FilterOperator.IN, values },
      })
    ),
  });

  const [sortBy, setSortBy] = useState<SortBy>({ ID: "desc" });

  const schemaItems: (SchemaEntry | boolean | undefined)[] = [
    {
      label: t("common:id"),
      accessor: "ID",
      dataType: DataType.STRING,
      sortable: true,
      flex: 1,
    },
    {
      limitedWidthClass: "w-80",
      label: t("common:description"),
      accessor: "description",
      dataType: DataType.STRING,
      sortable: true,
      flex: 2,
    },
  ];

  const lowerCasedSearchString = searchString.toLowerCase();
  const formattedData: RowData[] = values
    .map((v) => ({
      ID: v,
      description: getDescriptionForID(v, data),
    }))
    .sort((a, b) => {
      // sort by ID or description based on the current sortBy state
      const sortKey = Object.keys(sortBy)[0] as keyof SortBy;
      const sortDirection = sortBy[sortKey];
      const aValue = (a as SortBy)[sortKey];
      const bValue = (b as SortBy)[sortKey];
      if (sortDirection === "asc") {
        return aValue.localeCompare(bValue);
      }

      return bValue.localeCompare(aValue);
    })
    .filter(
      ({ ID, description }) =>
        ID.toLowerCase().includes(lowerCasedSearchString) ||
        description?.toLowerCase().includes(lowerCasedSearchString)
    );
  const schema = schemaItems.filter(Boolean) as SchemaEntry[];

  const formatCell = (params: GridRenderCellParams) => {
    const { ID } = params.row;

    switch (params.field) {
      case "ID":
        return linkToClaimAnalytics ? (
          <ClaimAnalyticsLink attribute={fieldName} value={ID} />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div>
      {(typeof title === "string" && (
        <Label text={title} className="text-gray-500" />
      )) ||
        title}
      {!error && (
        <div data-testid="table">
          <MaterialTable
            pageKey="filter-description-table"
            schema={schema}
            data={formattedData}
            formatCell={formatCell}
            loading={isLoading}
            sort={sortBy}
            handleSorting={setSortBy}
            height={200}
            hideFooter
          />
        </div>
      )}
      {error && <APIError error={error} />}
    </div>
  );
};

export default FilterDescriptionTable;
